import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, Sun, MoonStar, CalendarCheck, Baby, Send, CheckCircle2, HelpCircle, Moon } from "lucide-react";
import { useLang } from "../i18n";
import { Cloud, Flower, Rainbow } from "./decor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL = "Nenisplayhousechildcare@gmail.com";
const SCHEDULE_ICONS = { "early-drop-off": Sun, "late-pick-up": MoonStar, "weekend-care": CalendarCheck, "overnight-care": Moon, other: HelpCircle };

const inputCls =
  "w-full rounded-2xl border border-forest/15 bg-white px-4 py-3 text-[15px] text-forest placeholder:text-forest/40 focus:outline-none focus:ring-2 focus:ring-mint focus:border-transparent transition";

const InfoRow = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-start gap-3" data-testid={testid}>
    <span className="w-10 h-10 shrink-0 rounded-full bg-white flex items-center justify-center shadow-soft">
      <Icon className="text-forest" style={{ width: 18, height: 18 }} />
    </span>
    <div>
      <p className="font-display font-semibold text-forest text-[15px]">{label}</p>
      <div className="text-forest/80 text-[15px] leading-relaxed">{children}</div>
    </div>
  </div>
);

export default function Contact() {
  const { s, lang } = useLang();
  const [form, setForm] = useState({
    parent_name: "",
    email: "",
    phone: "",
    child_age: "",
    preferred_start_date: "",
    schedules: [],
    message: "",
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSchedule = (en) =>
    setForm((f) => ({
      ...f,
      schedules: f.schedules.includes(en) ? f.schedules.filter((x) => x !== en) : [...f.schedules, en],
    }));

  const validate = () => {
    const e = {};
    if (!form.parent_name.trim()) e.parent_name = s.contact.err.name;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = s.contact.err.email;
    if (!form.message.trim()) e.message = s.contact.err.message;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setSent(true);
      toast.success(s.contact.toastOk);
      setForm({ parent_name: "", email: "", phone: "", child_age: "", preferred_start_date: "", schedules: [], message: "", website: "" });
    } catch {
      toast.error(s.contact.toastErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 sm:py-24 overflow-hidden">
      <Cloud className="absolute left-[4%] top-10 w-24 opacity-80 animate-drift" />
      <Rainbow className="absolute right-[4%] top-12 w-28 opacity-80 animate-floaty" />
      <Flower className="absolute left-[10%] bottom-12 w-9 animate-sway" petal="#F0A28C" />
      <Flower className="absolute right-[12%] bottom-16 w-8" petal="#B8D8BD" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-display text-sm font-semibold tracking-wide text-forest/60 uppercase">
            {s.contact.eyebrow}
          </span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
            <span className="text-sageBlue">{s.contact.h1}</span>
            <span className="text-coral">{s.contact.h2}</span>
          </h2>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* info */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-6"
          >
            <InfoRow icon={Phone} label={s.contact.callLabel} testid="contact-phone">
              <a href="tel:+19174960201" data-testid="contact-phone-link" className="underline underline-offset-2 hover:text-forest font-bold">
                +1 (917) 496-0201
              </a>
            </InfoRow>
            <InfoRow icon={Mail} label={s.contact.emailLabel} testid="contact-email">
              <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="underline underline-offset-2 hover:text-forest break-all">
                {EMAIL}
              </a>
            </InfoRow>
            <InfoRow icon={Clock} label={s.contact.hoursLabel} testid="contact-hours">
              {s.contact.hoursVal}
            </InfoRow>
            <InfoRow icon={MapPin} label={s.contact.addressLabel} testid="contact-address">
              {s.contact.addressVal}
            </InfoRow>
            <div className="rounded-3xl bg-white/70 shadow-soft p-6 space-y-2.5" data-testid="flex-care-summary">
              <p className="font-display font-semibold text-forest text-[15px]">{s.contact.flexLabel}</p>
              {[
                { icon: Sun, label: s.contact.flexItems[0] },
                { icon: MoonStar, label: s.contact.flexItems[1] },
                { icon: CalendarCheck, label: s.contact.flexItems[2] },
                { icon: Moon, label: s.contact.flexItems[3] },
              ].map(({ icon: Icon, label }) => (
                <p key={label} className="flex items-center gap-2 text-forest/80 text-[14px]">
                  <Icon className="w-4 h-4 text-forest/70" /> {label}
                </p>
              ))}
            </div>

            <div className="flex flex-col gap-3 pt-1">
              <a
                data-testid="contact-email-us-button"
                href={`mailto:${EMAIL}`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-darksage text-white font-display font-medium px-6 py-3 hover:bg-[#4a338f] transition"
              >
                <Mail className="w-4 h-4" /> {s.contact.btnEmail}
              </a>
              <div className="grid grid-cols-2 gap-3">
                <a
                  data-testid="contact-schedule-tour-button"
                  href="mailto:Nenisplayhousechildcare@gmail.com?subject=Tour%20Request%20-%20Neni%27s%20Playhouse%20Childcare"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-pPink text-forest font-display font-medium px-5 py-3 hover:bg-pink-200 transition"
                >
                  <Baby className="w-4 h-4" /> {s.contact.btnTour}
                </a>
                <a
                  data-testid="contact-ask-enrollment-button"
                  href="mailto:Nenisplayhousechildcare@gmail.com?subject=Enrollment%20Inquiry%20-%20Neni%27s%20Playhouse%20Childcare"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-pBlue text-forest font-display font-medium px-5 py-3 hover:bg-sky-200 transition"
                >
                  <Send className="w-4 h-4" /> {s.contact.btnEnroll}
                </a>
              </div>
            </div>
          </motion.div>

          {/* form */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3 rounded-3xl bg-white shadow-card p-6 sm:p-8"
          >
            {sent ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="w-14 h-14 text-mint mx-auto" />
                <h3 className="font-display text-2xl font-semibold text-forest mt-4">{s.contact.okTitle}</h3>
                <p className="text-forest/80 mt-2 leading-relaxed">
                  {s.contact.okPre}
                  <a className="font-bold underline" href="tel:+19174960201">(917) 496-0201</a>.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="mt-6 rounded-full bg-darksage text-white font-display font-medium px-6 py-2.5 hover:bg-[#4a338f] transition"
                >
                  {s.contact.okBtn}
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="parent_name" className="font-display font-medium text-forest text-sm">{s.contact.labels.name}</label>
                    <input
                      id="parent_name"
                      data-testid="contact-field-name"
                      className={`${inputCls} mt-1.5`}
                      placeholder={s.contact.ph.name}
                      value={form.parent_name}
                      onChange={(e) => set("parent_name", e.target.value)}
                    />
                    {errors.parent_name && <p data-testid="contact-error-name" className="text-sm text-red-500 mt-1">{errors.parent_name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="font-display font-medium text-forest text-sm">{s.contact.labels.email}</label>
                    <input
                      id="email"
                      type="email"
                      data-testid="contact-field-email"
                      className={`${inputCls} mt-1.5`}
                      placeholder={s.contact.ph.email}
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                    />
                    {errors.email && <p data-testid="contact-error-email" className="text-sm text-red-500 mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className="font-display font-medium text-forest text-sm">{s.contact.labels.phone}</label>
                    <input
                      id="phone"
                      type="tel"
                      data-testid="contact-field-phone"
                      className={`${inputCls} mt-1.5`}
                      placeholder={s.contact.ph.phone}
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="child_age" className="font-display font-medium text-forest text-sm">{s.contact.labels.age}</label>
                    <input
                      id="child_age"
                      data-testid="contact-field-child-age"
                      className={`${inputCls} mt-1.5`}
                      placeholder={s.contact.ph.age}
                      value={form.child_age}
                      onChange={(e) => set("child_age", e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="start_date" className="font-display font-medium text-forest text-sm">{s.contact.labels.date}</label>
                    <input
                      id="start_date"
                      type="date"
                      data-testid="contact-field-start-date"
                      className={`${inputCls} mt-1.5`}
                      value={form.preferred_start_date}
                      onChange={(e) => set("preferred_start_date", e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <span className="font-display font-medium text-forest text-sm">{s.contact.labels.schedule}</span>
                    <div className="grid sm:grid-cols-2 gap-2 mt-2">
                      {s.contact.schedules.map((opt) => {
                        const Icon = SCHEDULE_ICONS[opt.slug] || Clock;
                        const active = form.schedules.includes(opt.en);
                        return (
                          <button
                            type="button"
                            key={opt.slug}
                            data-testid={`schedule-option-${opt.slug}`}
                            aria-pressed={active}
                            onClick={() => toggleSchedule(opt.en)}
                            className={`flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-[14px] font-semibold transition ${
                              active
                                ? "border-mint bg-sage text-forest"
                                : "border-forest/15 bg-cream/60 text-forest/70 hover:border-mint"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            {lang === "es" ? opt.es : opt.en}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className="font-display font-medium text-forest text-sm">{s.contact.labels.message}</label>
                    <textarea
                      id="message"
                      rows="4"
                      data-testid="contact-field-message"
                      className={`${inputCls} mt-1.5 resize-none`}
                      placeholder={s.contact.ph.msg}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                    />
                    {errors.message && <p data-testid="contact-error-message" className="text-sm text-red-500 mt-1">{errors.message}</p>}
                  </div>
                </div>

                {/* honeypot — hidden from humans, catches bots */}
                <div className="honeypot" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input id="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} />
                </div>

                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={sending}
                  className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-brown text-white font-display font-medium text-lg px-10 py-3.5 shadow-card hover:bg-[#4a338f] hover:scale-[1.02] transition-all duration-200 disabled:opacity-60"
                >
                  {sending ? s.contact.sending : s.contact.submit}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
